import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  PushPin,
  Campaign,
  Policy,
  Notifications,
  CheckCircle,
  Edit,
  Delete,
  Visibility
} from '@mui/icons-material';
import { FeedPost } from '@/services/OrganizationFeedService';

interface FeedCardProps {
  post: FeedPost;
  currentUserId: string;
  currentUserRole: string;
  onAcknowledge: (id: number) => void;
  onEdit?: (post: FeedPost) => void;
  onDelete?: (id: number) => void;
  onViewAcknowledgments?: (id: number) => void;
  compact?: boolean;
}

const categoryConfig = {
  announcement: {
    label: 'Announcement',
    color: 'primary' as const,
    icon: <Campaign sx={{ fontSize: 16 }} />
  },
  policy_update: {
    label: 'Policy Update',
    color: 'warning' as const,
    icon: <Policy sx={{ fontSize: 16 }} />
  },
  general_notice: {
    label: 'General Notice',
    color: 'info' as const,
    icon: <Notifications sx={{ fontSize: 16 }} />
  }
};

const priorityConfig = {
  low: { color: '#9e9e9e', label: 'Low' },
  normal: { color: '#2196f3', label: 'Normal' },
  high: { color: '#ff9800', label: 'High' },
  urgent: { color: '#f44336', label: 'Urgent' }
};

const FeedCard: React.FC<FeedCardProps> = ({
  post,
  currentUserId,
  currentUserRole,
  onAcknowledge,
  onEdit,
  onDelete,
  onViewAcknowledgments,
  compact = false
}) => {
  const theme = useTheme();
  const isAuthor = post.author_id === currentUserId;
  const canEdit = ['HR', 'Admin', 'Program Manager'].includes(currentUserRole) && isAuthor;
  const canDelete = ['HR', 'Admin', 'Program Manager'].includes(currentUserRole) && (isAuthor || currentUserRole === 'Admin');
  const canViewAcknowledgments = ['HR', 'Admin'].includes(currentUserRole);

  const category = categoryConfig[post.category];
  const priority = priorityConfig[post.priority];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <Card
      sx={{
        mb: 2,
        border: post.priority === 'urgent'
          ? `2px solid ${theme.palette.error.main}`
          : `1px solid ${theme.palette.divider}`,
        backgroundColor: post.is_pinned
          ? theme.palette.mode === 'dark'
            ? 'rgba(255, 193, 7, 0.08)'
            : 'rgba(255, 193, 7, 0.05)'
          : theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: compact ? 2 : 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {post.is_pinned && (
              <Chip
                icon={<PushPin sx={{ fontSize: 14 }} />}
                label="Pinned"
                size="small"
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'inherit' }
                }}
              />
            )}
            <Chip
              icon={category.icon}
              label={category.label}
              size="small"
              color={category.color}
              variant="outlined"
            />
            {post.priority !== 'normal' && (
              <Chip
                label={priority.label}
                size="small"
                sx={{
                  backgroundColor: priority.color,
                  color: '#fff',
                  fontWeight: 600
                }}
              />
            )}
          </Box>

          {/* Actions */}
          {(canEdit || canDelete) && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && onEdit && (
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(post)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && onDelete && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => onDelete(post.id)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Title */}
        <Typography
          variant={compact ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 600,
            mb: 1,
            color: theme.palette.text.primary
          }}
        >
          {post.title}
        </Typography>

        {/* Author & Date */}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            mb: 2,
            display: 'block'
          }}
        >
          Posted by {post.author_name} • {formatDate(post.created_at)}
        </Typography>

        {/* Content */}
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            mb: 2,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6
          }}
        >
          {compact ? truncateContent(post.content, 150) : truncateContent(post.content, 300)}
        </Typography>

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          {/* Acknowledgment Status */}
          {post.requires_acknowledgment && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {post.user_acknowledged ? (
                <Chip
                  icon={<CheckCircle sx={{ fontSize: 16 }} />}
                  label="Acknowledged"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={() => onAcknowledge(post.id)}
                  sx={{ textTransform: 'none' }}
                >
                  Acknowledge
                </Button>
              )}
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {post.acknowledgment_count}/{post.total_employees} acknowledged
              </Typography>
            </Box>
          )}

          {/* View Acknowledgments (HR/Admin) */}
          {canViewAcknowledgments && post.requires_acknowledgment && onViewAcknowledgments && (
            <Button
              variant="text"
              size="small"
              startIcon={<Visibility />}
              onClick={() => onViewAcknowledgments(post.id)}
              sx={{ textTransform: 'none' }}
            >
              View Details
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeedCard;
